import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  ArrowRight,
  Upload,
  Sparkles,
  TrendingUp,
  ChevronDown,
  ChevronUp,
  ScanFace,
  Scissors,
  Droplets,
  Dumbbell,
  Glasses,
  Palette,
  Check,
  GripVertical,
  User,
} from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { LegalLinks } from "../components/LegalModal";

/* ─── Intersection Observer Hook ─── */
function useInView(ref, threshold = 0.12) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setInView(true);
          obs.disconnect();
        }
      },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [ref, threshold]);
  return inView;
}

function FadeIn({ children, className = "", delay = 0, direction = "up" }) {
  const ref = useRef(null);
  const inView = useInView(ref);
  const transforms = {
    up: "translateY(30px)",
    down: "translateY(-30px)",
    left: "translateX(30px)",
    right: "translateX(-30px)",
    none: "none",
  };
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? "none" : transforms[direction],
        transition: `opacity 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}s, transform 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}s`,
      }}
    >
      {children}
    </div>
  );
}

/* ─── Before/After Slider ─── */
function BeforeAfterSlider({ beforeImg, afterImg, name, objectPos = "center center" }) {
  const containerRef = useRef(null);
  const [sliderPos, setSliderPos] = useState(50);
  const isDragging = useRef(false);

  const updatePosition = useCallback((clientX) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = clientX - rect.left;
    const pct = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPos(pct);
  }, []);

  useEffect(() => {
    const onMove = (e) => {
      if (!isDragging.current) return;
      e.preventDefault();
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      updatePosition(clientX);
    };
    const onUp = () => { isDragging.current = false; };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, [updatePosition]);

  const onStart = (e) => {
    isDragging.current = true;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    updatePosition(clientX);
  };

  return (
    <div className="flex flex-col items-center gap-3">
      <div
        ref={containerRef}
        className="relative w-full aspect-[3/4] rounded-2xl overflow-hidden cursor-col-resize select-none border border-surface-border shadow-sm"
        onMouseDown={onStart}
        onTouchStart={onStart}
      >
        {/* After (full background) */}
        <img src={afterImg} alt={`${name} nachher`} className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: objectPos }} draggable={false} />
        {/* Before (clipped) */}
        <div className="absolute inset-0 overflow-hidden" style={{ clipPath: `inset(0 ${100 - sliderPos}% 0 0)` }}>
          <img src={beforeImg} alt={`${name} vorher`} className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: objectPos }} draggable={false} />
        </div>
        {/* Slider line + handle */}
        <div className="absolute top-0 bottom-0" style={{ left: `${sliderPos}%`, transform: 'translateX(-50%)' }}>
          <div className="w-0.5 h-full bg-white/80" />
          <div className="absolute left-1/2 -translate-x-1/2 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center" style={{ top: '75%' }}>
            <GripVertical size={18} className="text-primary" />
          </div>
        </div>
        {/* Labels */}
        <div className="absolute top-3 left-3 px-2 py-1 bg-black/50 rounded text-white text-xs font-medium backdrop-blur-sm">Vorher</div>
        <div className="absolute top-3 right-3 px-2 py-1 bg-black/50 rounded text-white text-xs font-medium backdrop-blur-sm">Nachher</div>
      </div>
      <span className="text-sm font-medium text-text-secondary">{name}</span>
    </div>
  );
}

/* ─── FAQ Item ─── */
function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-surface-border last:border-b-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-5 text-left group"
      >
        <span className="font-medium text-text-primary group-hover:text-accent-dark transition-colors pr-4">
          {q}
        </span>
        {open ? (
          <ChevronUp size={18} className="text-text-muted flex-shrink-0" />
        ) : (
          <ChevronDown size={18} className="text-text-muted flex-shrink-0" />
        )}
      </button>
      <div
        className="overflow-hidden transition-all duration-300"
        style={{ maxHeight: open ? "200px" : "0", opacity: open ? 1 : 0 }}
      >
        <p className="pb-5 text-text-secondary leading-relaxed text-[15px]">{a}</p>
      </div>
    </div>
  );
}

/* ─── User Menu (Navbar) ─── */
function UserMenu({ onLogin, onRegister, t }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={ref} className="relative hidden sm:block">
      <button
        onClick={() => setOpen(!open)}
        className="text-text-muted hover:text-primary transition-colors p-1"
      >
        <User size={20} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 bg-white border border-surface-border rounded-xl shadow-lg py-1 min-w-[160px] z-50">
          <button
            onClick={() => { onLogin(); setOpen(false); }}
            className="w-full text-left px-4 py-2.5 text-sm text-text-secondary hover:bg-surface transition-colors"
          >
            {t("nav_login")}
          </button>
          <button
            onClick={() => { onRegister(); setOpen(false); }}
            className="w-full text-left px-4 py-2.5 text-sm text-text-secondary hover:bg-surface transition-colors"
          >
            {t("auth_register")}
          </button>
        </div>
      )}
    </div>
  );
}

/* ─── Auth Modal ─── */
function AuthModal({ mode, onClose, onSuccess, t }) {
  const { login, register, loading } = useAuth();
  const [isLogin, setIsLogin] = useState(mode === "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!isLogin && !name.trim()) { setError(t("auth_error_name")); return; }
    if (!email.trim()) { setError(t("auth_error_email")); return; }
    if (!password) { setError(t("auth_error_password")); return; }
    try {
      if (isLogin) {
        await login(email, password);
      } else {
        await register(email, password, name);
      }
      onSuccess();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full mx-4 p-8" onClick={(e) => e.stopPropagation()}>
        <div className="text-center mb-6">
          <img src="/logo.png" alt="Beauly" className="h-16 object-contain mx-auto mb-3" />
          <h2 className="font-display text-2xl text-primary">{isLogin ? t("auth_login") : t("auth_register")}</h2>
        </div>
        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          {!isLogin && (
            <input
              type="text"
              placeholder={t("auth_name")}
              value={name}
              onChange={(e) => { setName(e.target.value); setError(""); }}
              className={`w-full px-4 py-3 rounded-lg bg-surface border ${error && !name.trim() && !isLogin ? 'border-red-400' : 'border-surface-borderDark'} text-primary placeholder-text-placeholder focus:border-accent focus:ring-2 focus:ring-primary-50 outline-none transition`}
            />
          )}
          <input
            type="email"
            placeholder={t("auth_email")}
            value={email}
            onChange={(e) => { setEmail(e.target.value); setError(""); }}
            className={`w-full px-4 py-3 rounded-lg bg-surface border ${error && !email.trim() ? 'border-red-400' : 'border-surface-borderDark'} text-primary placeholder-text-placeholder focus:border-accent focus:ring-2 focus:ring-primary-50 outline-none transition`}
          />
          <input
            type="password"
            placeholder={t("auth_password")}
            value={password}
            onChange={(e) => { setPassword(e.target.value); setError(""); }}
            className={`w-full px-4 py-3 rounded-lg bg-surface border ${error && !password ? 'border-red-400' : 'border-surface-borderDark'} text-primary placeholder-text-placeholder focus:border-accent focus:ring-2 focus:ring-primary-50 outline-none transition`}
          />
          {error && <p className="text-red-500 text-sm">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-accent text-white rounded-lg font-medium hover:bg-accent-hover transition-colors disabled:opacity-50"
          >
            {loading ? "..." : isLogin ? t("auth_login") : t("auth_register")}
          </button>
        </form>
        <p className="text-center text-sm text-text-muted mt-4">
          <button onClick={() => { setIsLogin(!isLogin); setError(""); }} className="text-accent-dark hover:underline">
            {isLogin ? t("auth_no_account") : t("auth_has_account")}
          </button>
        </p>
      </div>
    </div>
  );
}

/* ─── Main Landing Page ─── */
export default function LandingPage({ onGetStarted }) {
  const { t } = useLanguage();
  const { token } = useAuth();
  const [authModal, setAuthModal] = useState(null);

  const STEPS = [
    { num: "01", title: t("step_1_title"), desc: t("step_1_desc"), icon: Upload },
    { num: "02", title: t("step_2_title"), desc: t("step_2_desc"), icon: ScanFace },
    { num: "03", title: t("step_3_title"), desc: t("step_3_desc"), icon: Sparkles },
    { num: "04", title: t("step_4_title"), desc: t("step_4_desc"), icon: TrendingUp },
  ];

  const CATEGORIES = [
    { icon: Droplets, title: t("cat_skin_title"), desc: t("cat_skin_desc") },
    { icon: Scissors, title: t("cat_hair_title"), desc: t("cat_hair_desc") },
    { icon: ScanFace, title: t("cat_face_title"), desc: t("cat_face_desc") },
    { icon: Glasses, title: t("cat_glasses_title"), desc: t("cat_glasses_desc") },
    { icon: Palette, title: t("cat_beard_title"), desc: t("cat_beard_desc") },
    { icon: Dumbbell, title: t("cat_fitness_title"), desc: t("cat_fitness_desc") },
  ];

  const FAQS = [
    { q: t("faq_1_q"), a: t("faq_1_a") },
    { q: t("faq_2_q"), a: t("faq_2_a") },
    { q: t("faq_3_q"), a: t("faq_3_a") },
    { q: t("faq_4_q"), a: t("faq_4_a") },
    { q: t("faq_5_q"), a: t("faq_5_a") },
  ];

  const trustItems = t("trust_items") || [];

  return (
    <div className="bg-white text-text-primary min-h-screen font-sans antialiased">
      {/* ── Navbar ── */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg border-b border-surface-border">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="Beauly" className="h-9 object-contain" />
            <span className="font-display text-2xl text-primary">Beauly</span>
          </div>
          <div className="hidden md:flex items-center gap-8 text-sm text-text-muted">
            <a href="#how" className="hover:text-primary transition-colors">{t("nav_how")}</a>
            <a href="#features" className="hover:text-primary transition-colors">{t("nav_features")}</a>
          </div>
          <div className="flex items-center gap-3">
            <LanguageSwitcher dropUp={false} />
            <UserMenu onLogin={() => setAuthModal("login")} onRegister={() => setAuthModal("register")} t={t} />
            <button
              onClick={() => token ? onGetStarted() : setAuthModal("login")}
              className="px-5 py-2 bg-accent text-white rounded-lg text-sm font-medium hover:bg-accent-hover transition-colors"
            >
              {t("nav_cta")}
            </button>
          </div>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-20 px-6 bg-surface-warm">
        <div className="max-w-2xl mx-auto text-center">
          <FadeIn delay={0.1}>
            <h1 className="font-display text-[clamp(2.2rem,5vw,3.8rem)] leading-[1.08] tracking-tight mb-6 text-primary">
              {t("hero_title_1")}{" "}
              <span className="italic">{t("hero_title_2")}</span>{" "}
              {t("hero_title_3")}
            </h1>
          </FadeIn>

          <FadeIn delay={0.2}>
            <p className="text-text-secondary text-base md:text-lg max-w-lg mx-auto mb-10 leading-relaxed">
              {t("hero_desc")}
            </p>
          </FadeIn>

          <FadeIn delay={0.3}>
            <div className="flex justify-center">
              <button
                onClick={() => token ? onGetStarted() : setAuthModal("login")}
                className="group flex items-center gap-2 px-8 py-4 bg-primary text-white rounded-xl font-medium text-lg hover:bg-primary-600 transition-all duration-300"
              >
                {t("hero_cta_join") || "Tritt dem Club bei"}
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ── Before / After Showcase ── */}
      <section className="pb-20 md:pb-28 px-6 bg-surface-warm">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 md:gap-8 items-start">
            <FadeIn delay={0.1}>
              <BeforeAfterSlider beforeImg="/Lebron Vorher.jpg" afterImg="/Lebron Nachher.png" name="Yang" />
            </FadeIn>
            <FadeIn delay={0.2}>
              <BeforeAfterSlider beforeImg="/Sara Vorher.png" afterImg="/Sara Nachher.png" name="Sara" objectPos="center 15%" />
            </FadeIn>
            <FadeIn delay={0.3}>
              <BeforeAfterSlider beforeImg="/Bozo1.png" afterImg="/bozo2.jpeg" name="James" />
            </FadeIn>
          </div>
          <FadeIn delay={0.4}>
            <div className="text-center mt-10">
              <p className="text-accent-dark text-sm font-medium tracking-widest uppercase mb-3">{t("ba_label") || "Ergebnisse"}</p>
              <h2 className="font-display text-[clamp(1.8rem,4vw,2.8rem)] leading-tight tracking-tight text-primary">
                {t("ba_title") || "Sieh die Veränderung"}
              </h2>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ── How it Works ── */}
      <section id="how" className="py-24 md:py-32 px-6">
        <div className="max-w-6xl mx-auto">
          <FadeIn>
            <div className="max-w-2xl mb-16">
              <p className="text-accent-dark text-sm font-medium tracking-widest uppercase mb-3">{t("steps_label")}</p>
              <h2 className="font-display text-[clamp(2rem,4vw,3.2rem)] leading-tight tracking-tight text-primary">
                {t("steps_title_1")}{" "}
                <span className="italic">{t("steps_title_2")}</span>
              </h2>
            </div>
          </FadeIn>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS.map((step, i) => (
              <FadeIn key={step.num} delay={i * 0.1}>
                <div className="group relative p-6 rounded-2xl bg-surface border border-surface-border hover:border-primary-100 transition-all duration-300 h-full">
                  <span className="block text-primary-300 font-mono text-sm font-medium mb-4">
                    {step.num} /
                  </span>
                  <step.icon size={24} className="text-accent-dark mb-4" strokeWidth={1.5} />
                  <h3 className="font-semibold text-primary mb-2">{step.title}</h3>
                  <p className="text-text-muted text-sm leading-relaxed">{step.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ── Old Way vs New Way ── */}
      <section className="py-24 md:py-32 px-6 bg-surface-warm">
        <div className="max-w-5xl mx-auto">
          <FadeIn>
            <div className="text-center mb-16">
              <p className="text-accent-dark text-sm font-medium tracking-widest uppercase mb-3">{t("compare_label")}</p>
              <h2 className="font-display text-[clamp(2rem,4vw,3.2rem)] leading-tight tracking-tight text-primary">
                {t("compare_title_1")} <span className="italic">{t("compare_title_2")}</span>
              </h2>
            </div>
          </FadeIn>

          <div className="grid md:grid-cols-2 gap-8">
            <FadeIn delay={0.1}>
              <div className="p-8 rounded-2xl bg-white border border-surface-borderDark h-full">
                <h3 className="font-display text-xl mb-6 text-text-muted">{t("old_title")}</h3>
                <ul className="space-y-4">
                  {(t("old_items") || []).map((item) => (
                    <li key={item} className="flex items-start gap-3 text-text-muted">
                      <span className="w-5 h-5 rounded-full bg-surface-borderDark flex items-center justify-center flex-shrink-0 mt-0.5 text-xs text-text-muted">✕</span>
                      <span className="text-[15px]">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </FadeIn>

            <FadeIn delay={0.2}>
              <div className="p-8 rounded-2xl bg-primary text-white h-full">
                <h3 className="font-display text-xl mb-6 text-white/70">{t("new_title")}</h3>
                <ul className="space-y-4">
                  {(t("new_items") || []).map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check size={12} />
                      </span>
                      <span className="text-[15px] text-white/90">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ── Features / Categories ── */}
      <section id="features" className="py-24 md:py-32 px-6">
        <div className="max-w-6xl mx-auto">
          <FadeIn>
            <div className="text-center max-w-2xl mx-auto mb-16">
              <p className="text-accent-dark text-sm font-medium tracking-widest uppercase mb-3">{t("cat_label")}</p>
              <h2 className="font-display text-[clamp(2rem,4vw,3.2rem)] leading-tight tracking-tight text-primary">
                {t("cat_title_1")}{" "}
                <span className="italic">{t("cat_title_2")}</span>
              </h2>
            </div>
          </FadeIn>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {CATEGORIES.map((cat, i) => (
              <FadeIn key={cat.title} delay={i * 0.08}>
                <div className="group p-6 rounded-2xl border border-surface-border hover:border-primary-100 bg-white transition-all duration-300 h-full">
                  <div className="w-10 h-10 rounded-xl bg-primary-50 flex items-center justify-center mb-4 group-hover:bg-primary-100 transition-colors">
                    <cat.icon size={20} className="text-accent-dark" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-semibold text-primary mb-2">{cat.title}</h3>
                  <p className="text-text-muted text-sm leading-relaxed">{cat.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ── Stats ── */}
      <section className="py-20 px-6 bg-primary">
        <div className="max-w-4xl mx-auto">
          <FadeIn>
            <div className="grid grid-cols-3 gap-8 text-center">
              {[
                { value: t("stat_1_value"), label: t("stat_1_label") },
                { value: t("stat_2_value"), label: t("stat_2_label") },
                { value: t("stat_3_value"), label: t("stat_3_label") },
              ].map((stat, i) => (
                <div key={stat.label} className="relative">
                  <div className="font-display text-[clamp(1.8rem,3.5vw,3rem)] text-white mb-1">
                    {stat.value}
                  </div>
                  <div className="text-white/50 text-xs tracking-widest uppercase">
                    {stat.label}
                  </div>
                  {i < 2 && (
                    <div className="absolute right-0 top-1/2 -translate-y-1/2 w-px h-10 bg-white/10 hidden md:block" />
                  )}
                </div>
              ))}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ── Testimonials ── */}
      <section className="py-24 md:py-32 overflow-hidden">
        <FadeIn className="px-6">
          <div className="text-center mb-16">
            <p className="text-accent-dark text-sm font-medium tracking-widest uppercase mb-3">{t("test_label")}</p>
            <h2 className="font-display text-[clamp(2rem,4vw,3.2rem)] leading-tight tracking-tight text-primary">
              {t("test_title_1")} <span className="italic">{t("test_title_2")}</span>
            </h2>
          </div>
        </FadeIn>

        <div className="relative overflow-hidden">
          <div className="flex gap-6 animate-marquee will-change-transform" style={{ width: 'max-content' }}>
            {[0, 1].map((setIdx) => {
              const reviews = [
                { text: t("test_1_text"), name: t("test_1_name"), loc: t("test_1_loc") },
                { text: t("test_2_text"), name: t("test_2_name"), loc: t("test_2_loc") },
                { text: t("test_3_text"), name: t("test_3_name"), loc: t("test_3_loc") },
                { text: t("test_4_text"), name: t("test_4_name"), loc: t("test_4_loc") },
                { text: t("test_5_text"), name: t("test_5_name"), loc: t("test_5_loc") },
                { text: t("test_6_text"), name: t("test_6_name"), loc: t("test_6_loc") },
              ];
              return reviews.map((item, i) => (
                <div key={`${setIdx}-${i}`} className="w-[320px] flex-shrink-0 p-6 rounded-2xl bg-surface border border-surface-border flex flex-col">
                  <p className="text-text-secondary text-[15px] leading-relaxed flex-1 mb-6">"{item.text}"</p>
                  <div>
                    <p className="font-medium text-primary text-sm">{item.name}</p>
                    <p className="text-text-muted text-xs">{item.loc}</p>
                  </div>
                </div>
              ));
            })}
          </div>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="py-24 md:py-32 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <FadeIn>
            <h2 className="font-display text-[clamp(2.2rem,5vw,3.8rem)] leading-[1.05] tracking-tight mb-6 text-primary">
              {t("cta_title_1")}{" "}
              <span className="italic">{t("cta_title_2")}</span>
            </h2>
            <p className="text-text-secondary text-lg mb-10 max-w-xl mx-auto">
              {t("cta_desc")}
            </p>
            <button
              onClick={() => token ? onGetStarted() : setAuthModal("register")}
              className="group inline-flex items-center gap-2 px-10 py-4 bg-primary text-white rounded-xl font-medium text-lg hover:bg-primary-600 transition-all duration-300"
            >
              {t("cta_button")}
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </FadeIn>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="border-t border-surface-border bg-primary py-6 px-6">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="Beauly" className="h-5 object-contain invert brightness-200" />
            <span className="font-display text-sm text-white">Beauly</span>
          </div>
          <div className="flex items-center gap-5 text-xs text-white/50">
            <a href="#how" className="hover:text-white transition-colors">{t("footer_how")}</a>
            <a href="#features" className="hover:text-white transition-colors">{t("footer_features")}</a>
            <LegalLinks dark />
          </div>
          <div className="text-white/30 text-xs">
            &copy; {new Date().getFullYear()} Beauly
          </div>
        </div>
      </footer>

      {/* Auth Modal */}
      {authModal && (
        <AuthModal
          mode={authModal}
          onClose={() => setAuthModal(null)}
          onSuccess={() => { setAuthModal(null); onGetStarted(); }}
          t={t}
        />
      )}
    </div>
  );
}
